/**
 * Traccar API Client (OpenAPI 3.1 – Traccar 6.11.1)
 * Security: BasicAuth (email + password). Optional: ApiKey (Bearer) via session token.
 * Endpoints: session, devices, positions, reports/route.
 * Position.speed laut Spec in Knoten → für Anzeige: knotsToKmh(speed).
 */

const getBaseUrl = () => {
  let url = process.env.REACT_APP_TRACCAR_BASE_URL || process.env.TRACCAR_BASE_URL;
  const isHttpsPage = typeof window !== 'undefined' && window.location?.protocol === 'https:';
  if (isHttpsPage && url && url.startsWith('http:')) {
    const httpsUrl = process.env.REACT_APP_TRACCAR_BASE_URL_HTTPS || process.env.TRACCAR_BASE_URL;
    if (httpsUrl && httpsUrl.startsWith('https')) url = httpsUrl;
  }
  if (url) return url.replace(/\/+$/, '');
  return 'http://64.226.94.1:8082';
};

const getApiBase = () => {
  const base = getBaseUrl();
  return base.endsWith('/api') ? base : `${base}/api`;
};

const getCredentials = () => ({
  email: process.env.REACT_APP_TRACCAR_EMAIL || process.env.TRACCAR_USERNAME || '',
  password: process.env.REACT_APP_TRACCAR_PASSWORD || process.env.TRACCAR_PASSWORD || '',
});

const getAuthHeaders = () => {
  const { email, password } = getCredentials();
  if (!email || !password) return {};
  const encoded = btoa(`${email}:${password}`);
  return { Authorization: `Basic ${encoded}` };
};

const apiFetch = async (path, options = {}) => {
  const base = getApiBase();
  const headers = { ...getAuthHeaders(), ...options.headers };
  const res = await fetch(`${base}${path}`, { ...options, headers, credentials: 'include' });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Traccar API ${res.status}: ${text || res.statusText}`);
  }
  const contentType = res.headers.get('content-type');
  if (contentType && contentType.includes('application/json')) return res.json();
  return res.text();
};

/** Geschwindigkeit: Traccar liefert Knoten (OpenAPI Position.speed), Umrechnung in km/h. */
export const knotsToKmh = (knots) => (knots == null ? null : knots * 1.852);

// --- Session (OpenAPI: Session) ---

/** POST /session – Create a new Session (application/x-www-form-urlencoded, email, password). */
export const sessionCreate = async () => {
  const { email, password } = getCredentials();
  if (!email || !password) throw new Error('Traccar: E-Mail und Passwort fehlen.');
  const body = new URLSearchParams({ email, password });
  return apiFetch('/session', {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: body.toString(),
  });
};

/** GET /session – Fetch Session information (optional ?token=). */
export const sessionGet = async (token) => {
  const q = token ? `?token=${encodeURIComponent(token)}` : '';
  return apiFetch(`/session${q}`);
};

/** DELETE /session – Close the Session. */
export const sessionClose = async () => {
  return apiFetch('/session', { method: 'DELETE' });
};

/** POST /session/token – Generate Session Token (optional expiration). Returns token string (text/plain). */
export const sessionTokenCreate = async (expiration) => {
  const body = expiration ? new URLSearchParams({ expiration }) : null;
  return apiFetch('/session/token', {
    method: 'POST',
    headers: body ? { 'Content-Type': 'application/x-www-form-urlencoded' } : {},
    body: body ? body.toString() : undefined,
  });
};

// --- Devices (OpenAPI: Devices) ---

/** GET /devices – Fetch a list of Devices. Params: all, userId, id, uniqueId, excludeAttributes. */
export const getDevices = async (params = {}) => {
  const q = new URLSearchParams();
  if (params.all != null) q.set('all', params.all);
  if (params.userId != null) q.set('userId', params.userId);
  if (params.id != null) q.set('id', params.id);
  if (params.uniqueId != null) q.set('uniqueId', params.uniqueId);
  if (params.excludeAttributes != null) q.set('excludeAttributes', params.excludeAttributes);
  const query = q.toString();
  return apiFetch(`/devices${query ? `?${query}` : ''}`);
};

// --- Positions (OpenAPI: Positions) ---

/** GET /positions – Last known positions for all user's Devices. Params: deviceId, from, to, id. */
export const getPositions = async (params = {}) => {
  const q = new URLSearchParams();
  if (params.deviceId != null) q.set('deviceId', params.deviceId);
  if (params.from != null) q.set('from', params.from);
  if (params.to != null) q.set('to', params.to);
  if (params.id != null) q.set('id', params.id);
  const query = q.toString();
  return apiFetch(`/positions${query ? `?${query}` : ''}`);
};

// --- Reports (OpenAPI: Reports) ---

/** GET /reports/route – Positions in time period. deviceId or groupId (array), from and to required (ISO 8601). */
export const getRoute = async (params) => {
  const { deviceId, groupId, from, to } = params;
  const q = new URLSearchParams();
  if (Array.isArray(deviceId)) deviceId.forEach((id) => q.append('deviceId', id));
  else if (deviceId != null) q.set('deviceId', deviceId);
  if (Array.isArray(groupId)) groupId.forEach((id) => q.append('groupId', id));
  else if (groupId != null) q.set('groupId', groupId);
  if (from != null) q.set('from', from);
  if (to != null) q.set('to', to);
  return apiFetch(`/reports/route?${q.toString()}`);
};

// --- Server (optional) ---

/** GET /server – Fetch Server information. */
export const getServer = async () => apiFetch('/server');

/** GET /server/geocode – Reverse geocode coordinates (latitude, longitude). */
export const geocode = async (latitude, longitude) =>
  apiFetch(`/server/geocode?latitude=${latitude}&longitude=${longitude}`);

// --- Legacy / compatibility ---

export const loginSession = sessionCreate;
export { getBaseUrl, getApiBase };

export default {
  knotsToKmh,
  getBaseUrl,
  getApiBase,
  sessionCreate,
  sessionGet,
  sessionClose,
  sessionTokenCreate,
  getDevices,
  getPositions,
  getRoute,
  getServer,
  geocode,
  loginSession,
};
